import axios, { AxiosHeaders, AxiosRequestConfig, isAxiosError } from "axios";
import { bumpAuthGeneration, currentAuthGeneration, endSession, readSharedAuthGeneration } from "./authSession";
import { useAuthStore } from "./store";
import { IAiSearchFilter, ICreateMedForm, ISignUpCertificate, ISignUpLicense, ISignUpPharmacy, ISignUpUser } from "./types";
import { compressImage } from "./utils";

export const API_BASE_URL = process.env.NODE_ENV === 'production' ? 'https://api.pharm-chat.com/' : 'http://localhost:5004/';

/* axios instance */

/*
 * 일반 API.
 * request / response interceptor가 붙는다.
 *
 * refresh와 logout은 여기로 보내지 않는다.
 * 이 instance의 401 처리가 /refresh를 다시 타면
 * interceptor가 자기 자신으로 들어온다.
 */

export const instance = axios.create({
    baseURL: API_BASE_URL,
    paramsSerializer: {
        serialize: (params) => {
            const searchParams = new URLSearchParams();
            
            Object.entries(params).forEach(([key, value]) => {
                if (value !== null && value !== undefined) {
                    if (Array.isArray(value)) {
                        // 배열인 경우 각 요소를 개별 파라미터로 추가
                        value.forEach(item => {
                            searchParams.append(key, item);
                        });
                    } else {
                        searchParams.append(key, value);
                    }
                }
            });
            
            return searchParams.toString();
        }
    }
});


/*
 * refresh, logout 전용.
 * interceptor를 붙이지 않는다.
 */

const authInstance = axios.create({
    baseURL: API_BASE_URL,
});

/* request interceptor with server token */

instance.interceptors.request.use(
    async (config) => {
        const server_token = localStorage.getItem("server_token");

        if (server_token) {
            config.headers.set(
                "Authorization",
                `Bearer ${server_token}`
            );
        }

        return config;
    },

    async (error) => {
        return Promise.reject(error);
    }
);

/* axios request config with retry */

interface AxiosRequestConfigWithRetry extends AxiosRequestConfig {
    _retry?: boolean;
}

/*
 * access token 401 가운데 refresh로 복구되는 code.
 * 403과 그 외 401은 여기서 빠진다.
 */

const REFRESHABLE_ACCESS_CODES = new Set([
    "NO_ACCESS_TOKEN",
    "ACCESS_TOKEN_EXPIRED",
    "ACCESS_TOKEN_INVALID",
]);

const TERMINAL_REFRESH_CODE = "REFRESH_SESSION_INVALID";

type RefreshResult = {
    serverToken: string;
    refreshToken: string;
};

/*
 * 로컬에 refresh token이 없을 때.
 * 서버 /refresh는 호출하지 않고 세션을 끝낸다.
 */

class MissingRefreshTokenError extends Error {
    constructor() {
        super("MISSING_REFRESH_TOKEN");
        this.name = "MissingRefreshTokenError";
    }
}

/*
 * refresh가 서버에 도착하기 전후에 로그아웃이나 다른 로그인이 끼어든 때.
 * 응답 토큰을 저장하지 않는다. 지금 세션을 여기서 끝내지 않는다.
 */

class AuthSessionChangedError extends Error {
    constructor() {
        super("AUTH_SESSION_CHANGED");
        this.name = "AuthSessionChangedError";
    }
}

/*
 * 동시에 여러 요청이 401이어도 refresh는 이 promise 하나로 모은다.
 * 끝나면 null로 돌려, 다음 만료에서 다시 한 번만 호출되게 한다.
 */

let refreshPromise: Promise<RefreshResult> | null = null;

const readErrorCode = (error: unknown): string | undefined => {
    if (!isAxiosError(error)) return undefined;

    const data = error.response?.data;

    if (!data || typeof data !== "object") return undefined;

    const code = (data as { code?: unknown }).code;

    return typeof code === "string" ? code : undefined;
};

/*
 * terminal: 로컬 refresh token 없음, 또는 401 REFRESH_SESSION_INVALID.
 * 네트워크, timeout, 5xx는 여기 들어오지 않는다.
 */

const isTerminalRefreshFailure = (error: unknown) => {
    if (error instanceof MissingRefreshTokenError) return true;

    if (!isAxiosError(error)) return false;

    return (
        error.response?.status === 401 &&
        readErrorCode(error) === TERMINAL_REFRESH_CODE
    );
};

const deleteStoredTokenIfMatches = (
    key: "server_token" | "refresh_token",
    expected: string
) => {
    if (localStorage.getItem(key) === expected) {
        localStorage.removeItem(key);
    }
};

/*
 * 로그인 저장과 refresh 저장이 localStorage를 동시에 쓰지 않게 한다.
 * 네트워크 요청은 이 잠금 밖에서 보낸다.
 */

let authMutation: Promise<unknown> = Promise.resolve();

const runAuthMutation = <T>(task: () => Promise<T>): Promise<T> => {
    const run = authMutation.then(task, task);

    authMutation = run.then(
        () => undefined,
        () => undefined
    );

    return run;
};

const refreshTokens = async (): Promise<RefreshResult> => {
    const generationAtStart = currentAuthGeneration();
    const sharedGenerationAtStart = readSharedAuthGeneration();
    const refreshTokenUsed = localStorage.getItem("refresh_token");
    const refreshPath = useAuthStore.getState().isAdmin ? "/admin/refresh" : "/refresh";

    const sessionStillCurrent = () => {
        return currentAuthGeneration() === generationAtStart
            && readSharedAuthGeneration() === sharedGenerationAtStart;
    };

    if (!refreshTokenUsed) {
        throw new MissingRefreshTokenError();
    }

    /*
     * 토큰을 읽는 동안 로그아웃/로그인이 시작됐으면 서버 rotation을 태우지 않는다.
     */

    if (!sessionStillCurrent()) {
        throw new AuthSessionChangedError();
    }

    const refreshResponse = await authInstance.post(
        refreshPath,
        { refresh_token: refreshTokenUsed },
        {
            timeout: 10_000,
        }
    );

    const new_server_token = refreshResponse.data?.server_token;
    const new_refresh_token = refreshResponse.data?.refresh_token;

    if (
        typeof new_server_token !== "string" ||
        new_server_token.length === 0 ||
        typeof new_refresh_token !== "string" ||
        new_refresh_token.length === 0
    ) {
        throw new Error("INVALID_REFRESH_RESPONSE");
    }

    /*
     * 저장 직전에 로그아웃/로그인과 직렬화한다.
     * 잠금 안에서는 localStorage와 generation만 다룬다.
     */

    const saved = await runAuthMutation(async (): Promise<
        | RefreshResult
        | { discardRefreshToken: string }
    > => {
        const currentRefreshToken = localStorage.getItem("refresh_token");

        if (
            currentRefreshToken !== refreshTokenUsed ||
            !sessionStillCurrent()
        ) {
            return { discardRefreshToken: new_refresh_token };
        }

        /*
         * refresh token을 먼저 쓴다.
         * access token만 남고 앱이 죽으면, 서버는 이미 이전 refresh를 폐기한 상태다.
         * refresh를 먼저 남기면 다음 401에서 그 토큰으로 다시 받을 수 있다.
         */

        localStorage.setItem("refresh_token", new_refresh_token);

        if (!sessionStillCurrent()) {
            deleteStoredTokenIfMatches("refresh_token", new_refresh_token);
            return { discardRefreshToken: new_refresh_token };
        }

        localStorage.setItem("server_token", new_server_token);

        if (!sessionStillCurrent()) {
            deleteStoredTokenIfMatches("refresh_token", new_refresh_token);
            deleteStoredTokenIfMatches("server_token", new_server_token);
            return { discardRefreshToken: new_refresh_token };
        }

        return {
            serverToken: new_server_token,
            refreshToken: new_refresh_token,
        };
    });

    /*
     * 서버는 이미 rotation을 끝냈다.
     * 잠금을 푼 뒤에, 저장하지 않은 refresh token으로만 세션을 폐기한다.
     * 실패해도 로컬 상태는 되돌리지 않는다.
     * 그 사이 새 로그인이 넣은 토큰은 값이 다르므로 여기서 지우지 않는다.
     */

    if ("discardRefreshToken" in saved) {
        await revokeDiscardedRefresh(saved.discardRefreshToken);
        throw new AuthSessionChangedError();
    }

    return saved;
};

const getOrStartRefresh = () => {
    if (!refreshPromise) {
        refreshPromise = refreshTokens().finally(() => {
            refreshPromise = null;
        });
    }

    return refreshPromise;
};

/* response interceptor with refresh token */

instance.interceptors.response.use(
    (response) => response,

    async (error) => {
        const originalRequest = error.config as AxiosRequestConfigWithRetry | undefined;

        ///////////////////////////////////////////////////////////////////////// 요청 정보 자체가 없으면 그냥 reject

        if (!originalRequest) return Promise.reject(error);

        ///////////////////////////////////////////////////////////////////////// 네트워크 에러, timeout (response 자체가 없음)

        if (!error.response) return Promise.reject(error);

        ///////////////////////////////////////////////////////////////////////// refresh / logout이 이 interceptor로 들어오면 재진입하지 않는다

        const url = originalRequest.url ?? "";

        if (url.includes("/refresh") || url.includes("/auth/logout")) {
            return Promise.reject(error);
        }

        ///////////////////////////////////////////////////////////////////////// 상태 코드와 code

        const status = error.response.status;
        const data = error.response.data as { code?: string } | undefined;
        const code = typeof data?.code === "string" ? data.code : undefined;

        ///////////////////////////////////////////////////////////////////////// access auth failure에서만 refresh

        const shouldRefresh =
            status === 401 &&
            code !== undefined &&
            REFRESHABLE_ACCESS_CODES.has(code) &&
            !originalRequest._retry;

        if (!shouldRefresh) return Promise.reject(error);

        /*
         * 재시도 전에 찍는다.
         * 새 토큰으로 다시 401이 나도 같은 요청은 refresh로 돌아가지 않는다.
         */

        originalRequest._retry = true;

        if (useAuthStore.getState().isGuest) {
            await endSession();
            return Promise.reject(error);
        }

        try {
            const refreshed = await getOrStartRefresh();

            const headers =
                originalRequest.headers instanceof AxiosHeaders
                    ? originalRequest.headers
                    : new AxiosHeaders();

            headers.set(
                "Authorization",
                `Bearer ${refreshed.serverToken}`,
                true
            );
            originalRequest.headers = headers;

            return instance(originalRequest);

        } catch (refreshError) {
            if (isTerminalRefreshFailure(refreshError)) {
                await endSession();

                /*
                 * refresh token이 없어 서버를 호출하지 않은 경우,
                 * caller에게는 원래 401을 넘긴다.
                 */
                if (refreshError instanceof MissingRefreshTokenError) {
                    return Promise.reject(error);
                }
            }

            ///////////////////////////////////////////////////////////////////////// 세션 변경, 5xx, 네트워크는 토큰을 지우지 않고 요청만 실패시킨다

            return Promise.reject(refreshError);
        }
    }
);

/*
 * 로그인 성공 토큰 저장.
 * refresh를 먼저 쓰고, 이어서 access를 쓴다.
 * access 저장이 실패하면 방금 쓴 refresh만 되돌린다.
 */

export const saveAuthTokens = async (
    server_token: string,
    refresh_token: string
) => {
    await runAuthMutation(async () => {
        bumpAuthGeneration();

        localStorage.setItem("refresh_token", refresh_token);

        try {
            localStorage.setItem("server_token", server_token);
        } catch (error) {
            try {
                deleteStoredTokenIfMatches("refresh_token", refresh_token);
            } catch (cleanupError) {
                console.error("Auth token rollback failed:", cleanupError);
            }

            throw error;
        }
    });
};

/*
 * 비회원은 refresh token이 없다.
 * 이전 로그인에서 남은 refresh token을 지우고 access token만 남긴다.
 */

export const saveGuestAccessToken = async (server_token: string) => {
    await runAuthMutation(async () => {
        bumpAuthGeneration();
        localStorage.removeItem("refresh_token");
        localStorage.setItem("server_token", server_token);
    });
};

/*
 * 직접 로그아웃의 서버 세션 폐기.
 * POST /auth/logout. body의 refresh token으로 그 AuthSession만 revoke한다.
 * authInstance라서 401 interceptor에 다시 들어가지 않는다.
 */

export const logoutSession = async (refreshToken: string) => {
    await authInstance.post(
        "/auth/logout",
        { refresh_token: refreshToken },
        { timeout: 5000 }
    );
};

/*
 * 저장하지 않은 refresh token만 폐기한다.
 * 실패해도 로컬에 방금 로그인한 토큰은 지우지 않는다.
 */

const revokeDiscardedRefresh = async (refreshToken: string) => {
    try {
        await logoutSession(refreshToken);
    } catch (error) {
        console.error("Discarded refresh revoke failed:", error);
    }
};

/* -------------------------------------------------------------- */

















/* -------------------------------------------------------------- */

/* signUpForOwner */

interface ISignUpForOwnerProps {
    user:ISignUpUser;
    pharmacy?:ISignUpPharmacy;
}

export const signUpForOwner = async ({ user, pharmacy }:ISignUpForOwnerProps) => {
    const response = await instance.post("/signup/owner", {
        user,
        pharmacy
    });
    return response.data;
}

/* signUpForPharmacist */

interface ISignUpForPharmacistProps {
    user:ISignUpUser;
}

export const signUpForPharmacist = async ({ user }:ISignUpForPharmacistProps) => {
    const response = await instance.post("/signup/pharmacist", { user });
    return response.data;
}

/* signUpForStudent */

interface ISignUpForStudentProps {
    user:ISignUpUser;
}

export const signUpForStudent = async ({ user }:ISignUpForStudentProps) => {
    const response = await instance.post("/signup/student", { user });
    return response.data;
}

/* signUp */

interface ISignUpProps {
    user:ISignUpUser;
    certificate?:ISignUpCertificate;
    license?:ISignUpLicense;
    pharmacy?:ISignUpPharmacy;
}

export const signUp = async ({ user, certificate, license, pharmacy }:ISignUpProps) => {
    const response = await instance.post("/signup", { user, certificate, license, pharmacy });
    return response.data;
}

/* checkPhoneDuplicate */

interface ICheckPhoneDuplicateProps {
    phone:string;
}

export const checkPhoneDuplicate = async ({ phone }:ICheckPhoneDuplicateProps) => {
    const response = await instance.post("/check-phone-duplicate", { phone });
    return response.data;
}

/* checkEmailDuplicate */

interface ICheckEmailDuplicateProps {
    email:string;
}

export const checkEmailDuplicate = async ({ email }:ICheckEmailDuplicateProps) => {
    const response = await instance.post("/check-email-duplicate", { email });
    return response.data;
}

/* checkDuplicate */

interface ICheckLicenseIdDuplicateProps {
    licenseId:string | null;
}

export const checkLicenseIdDuplicate = async ({ licenseId }:ICheckLicenseIdDuplicateProps) => {
    const response = await instance.post("/check-license-id-duplicate", { licenseId });
    return response.data;
}

/* checkHealthcareInstitutionIdDuplicate */

interface ICheckHealthcareInstitutionIdDuplicateProps {
    healthcareInstitutionId:string | null;
}

export const checkHealthcareInstitutionIdDuplicate = async ({ healthcareInstitutionId }:ICheckHealthcareInstitutionIdDuplicateProps) => {
    const response = await instance.post("/check-healthcare-institution-id-duplicate", { healthcareInstitutionId });
    return response.data;
}

/* signIn */

interface ISignInProps {
    email:string;
    password:string;
}

export const signIn = async ({ email, password }:ISignInProps) => {
    const response = await instance.post("/signin", {
        email,
        password
    });
    return response.data;
}

/* guestSignUp */

export const guestSignUp = async () => {
    const response = await instance.post("/signup/guest");
    return response.data;
}

/* updateSignUpInfoForOwner */

interface IUpdateSignUpInfoForOwnerProps {
    licenseId: string | null;
    healthcareInstitutionId: string | null;
    licenseImage: string | null;
    businessRegistrationCertImage: string | null;
    establishmentRegistrationCertImage: string | null;
}

export const updateSignUpInfoForOwner = async ({ licenseId, healthcareInstitutionId, licenseImage, businessRegistrationCertImage, establishmentRegistrationCertImage }:IUpdateSignUpInfoForOwnerProps) => {
    const response = await instance.put("/user/update-sign-up-info/owner", {
        licenseId,
        healthcareInstitutionId,
        licenseImage,
        businessRegistrationCertImage,
        establishmentRegistrationCertImage
    });
    return response.data;
}

/* updateSignUpInfoForPharmacist */

interface IUpdateSignUpInfoForPharmacistProps {
    licenseId: string | null;
    licenseImage: string | null;
}

export const updateSignUpInfoForPharmacist = async ({ licenseId, licenseImage }:IUpdateSignUpInfoForPharmacistProps) => {
    const response = await instance.put("/user/update-sign-up-info/pharmacist", {
        licenseId,
        licenseImage,
    });
    return response.data;
}

/* updateSignUpInfoForStudent */

interface IUpdateSignUpInfoForStudentProps {
    certificateImage: string | null;
}

export const updateSignUpInfoForStudent = async ({ certificateImage }:IUpdateSignUpInfoForStudentProps) => {
    const response = await instance.put("/user/update-sign-up-info/student", {
        certificateImage,
    });
    return response.data;
}

/* updateInfoForOwner */

interface IUpdateInfoForOwnerProps {
    licenseId: string | null;
    healthcareInstitutionId: string | null;
    licenseImage: string | null;
    businessRegistrationCertImage: string | null;
    establishmentRegistrationCertImage: string | null;
}

export const updateInfoForOwner = async ({ licenseId, healthcareInstitutionId, licenseImage, businessRegistrationCertImage, establishmentRegistrationCertImage }:IUpdateInfoForOwnerProps) => {
    const response = await instance.put("/user/update-info/owner", {
        licenseId,
        healthcareInstitutionId,
        licenseImage,
        businessRegistrationCertImage,
        establishmentRegistrationCertImage
    });
    return response.data;
}

/* updateInfoForPharmacist */

interface IUpdateInfoForPharmacistProps {
    licenseId: string | null;
    licenseImage: string | null;
}

export const updateInfoForPharmacist = async ({ licenseId, licenseImage }:IUpdateInfoForPharmacistProps) => {
    const response = await instance.put("/user/update-info/pharmacist", {
        licenseId,
        licenseImage,
    });
    return response.data;
}

/* getAuthCode */

interface IGetAuthCodeProps {
    phone:string;
}

export const getAuthCode = async ({ phone }:IGetAuthCodeProps) => {
    const response = await instance.post("/get-auth-code", {
        phone
    });
    return response.data;
}

/* resetAuthCode */

interface IResetAuthCodeProps {
    phone:string;
}

export const resetAuthCode = async ({ phone }:IResetAuthCodeProps) => {
    const response = await instance.post("/reset-auth-code", {
        phone
    });
    return response.data;
}

/* verifyAuthCode */

interface IVerifyAuthCodeProps {
    phone:string;
    authCode:string;
}

export const verifyAuthCode = async ({ phone, authCode }:IVerifyAuthCodeProps) => {
    const response = await instance.post("/verify-auth-code", {
        phone,
        authCode
    });
    return response.data;
}

/* me */

export const me = async () => {
    const response = await instance.get("/user/me");
    return response.data;
}

/* changePassword */

interface IChangePasswordProps { 
    legacyPassword:string;
    newPassword:string;
}

export const changePassword = async ({ legacyPassword, newPassword }:IChangePasswordProps) => {
    const response = await instance.put(`/user/change-password`, {
        legacyPassword,
        newPassword
    });
    return response.data;
}

/* quit */

interface IQuitProps {
    password:string;
    quitReason:string;
}

export const quit = async ({ password, quitReason }:IQuitProps) => {
    const response = await instance.delete(`/user/quit`, {
        data: {
            password,
            quitReason
        }
    });
    return response.data;
}

/* findId */

interface IFindIdProps {
    name:string;
    phone:string;
    authCode:string;
}
export const findId = async ({ name, phone, authCode }:IFindIdProps) => {
    const response = await instance.post(`/find-id`, {
        name,
        phone,
        authCode
    });
    return response.data;
}

/* resetPasswordVerification */

interface IResetPasswordVerificationProps {
    email:string;
    name:string;
    phone:string;
    authCode:string;
}

export const resetPasswordVerification = async ({ email, name, phone, authCode }:IResetPasswordVerificationProps) => {
    const response = await instance.post(`/reset-password-verification`, {
        email,
        name,
        phone,
        authCode
    });
    return response.data;
}

/* resetPassword */

interface IResetPasswordProps {
    email:string;
    newPassword:string;
}

export const resetPassword = async ({ email, newPassword }:IResetPasswordProps) => {
    const response = await instance.post(`/reset-password`, {
        email,
        newPassword
    });
    return response.data;
}

/* update user type — change-type flows (body shape은 서버와 맞춰 조정 가능) */

/* updateUserTypePharmacistToOwner */

export interface IUpdateUserTypePharmacistToOwnerProps {
    name: string;
    address: string;
    healthcareInstitutionId: string;
    businessRegistrationCertImage: string;
    establishmentRegistrationCertImage: string;
}

export const updateUserTypePharmacistToOwner = async ({ name, address, healthcareInstitutionId, businessRegistrationCertImage, establishmentRegistrationCertImage }: IUpdateUserTypePharmacistToOwnerProps) => {
    const response = await instance.put(`/user/update-user-type/pharmacist-to-owner`, {
        name,
        address,
        healthcareInstitutionId,
        businessRegistrationCertImage,
        establishmentRegistrationCertImage,
    });
    return response.data;
}

/* updateUserTypeStudentToPharmacist */

export interface IUpdateUserTypeStudentToPharmacistProps {
    licenseId: string;
    licenseImage: string;
    place: string;
}

export const updateUserTypeStudentToPharmacist = async ({ licenseId, licenseImage, place }: IUpdateUserTypeStudentToPharmacistProps) => {
    const response = await instance.put(`/user/update-user-type/student-to-pharmacist`, {
        licenseId,
        licenseImage,
        place,
    });
    return response.data;
}

/* updateUserTypeStudentToOwner */

export interface IUpdateUserTypeStudentToOwnerProps {
    licenseId: string;
    name: string;
    address: string;
    healthcareInstitutionId: string;
    businessRegistrationCertImage: string;
    establishmentRegistrationCertImage: string;
}

export const updateUserTypeStudentToOwner = async ({ licenseId, name, address, healthcareInstitutionId, businessRegistrationCertImage, establishmentRegistrationCertImage }: IUpdateUserTypeStudentToOwnerProps) => {
    const response = await instance.put(`/user/update-user-type/student-to-owner`, {
        licenseId,
        name,
        address,
        healthcareInstitutionId,
        businessRegistrationCertImage,
        establishmentRegistrationCertImage,
    });
    return response.data;
}

/* createCertificate */

export const createCertificate = async ({ image }: { image: string }) => {
    const response = await instance.post("/user/certificate", { image });
    return response.data;
}

/* updateCertificate */

export const updateCertificate = async ({ image }: { image: string }) => {
    const response = await instance.put("/user/certificate", { image });
    return response.data;
}

/* deleteCertificate */

export const deleteCertificate = async () => {
    const response = await instance.delete(`/user/certificate`);
    return response.data;
}

/* createLicense */

export const createLicense = async ({ image, number }: { image: string; number: string }) => {
    const response = await instance.post("/user/license", { image, number });
    return response.data;
}

/* updateLicense */

export const updateLicense = async ({
    image,
    number,
}: {
    image?: string;
    number?: string;
}) => {
    const response = await instance.put("/user/license", { image, number });
    return response.data;
}

/* deleteLicense */

export const deleteLicense = async () => {
    const response = await instance.delete(`/user/license`);
    return response.data;
}

/* createPharmacy */

export const createPharmacy = async ({
    name,
    address,
    bizRegNo,
    businessRegistrationCertImage,
    establishmentRegistrationCertImage,
    healthcareInstitutionId,
}: {
    name: string;
    address: string;
    bizRegNo: string;
    businessRegistrationCertImage: string;
    establishmentRegistrationCertImage: string;
    healthcareInstitutionId: string;
}) => {
    const response = await instance.post("/user/pharmacy", {
        name,
        address,
        bizRegNo,
        businessRegistrationCertImage,
        establishmentRegistrationCertImage,
        healthcareInstitutionId,
    });
    return response.data;
}

/* updatePharmacy */

export const updatePharmacy = async ({
    name,
    address,
    bizRegNo,
    businessRegistrationCertImage,
    establishmentRegistrationCertImage,
    healthcareInstitutionId,
}: {
    name?: string;
    address?: string;
    bizRegNo?: string;
    businessRegistrationCertImage?: string;
    establishmentRegistrationCertImage?: string;
    healthcareInstitutionId?: string;
}) => {
    const response = await instance.put("/user/pharmacy", {
        name,
        address,
        bizRegNo,
        businessRegistrationCertImage,
        establishmentRegistrationCertImage,
        healthcareInstitutionId,
    });
    return response.data;
}

/* deletePharmacy */

export const deletePharmacy = async () => {
    const response = await instance.delete(`/user/pharmacy`);
    return response.data;
}

/* ------------------------------------------------------------------ */







/* getProfileSuspended */

export const getMyProfileSuspended = async () => {
    const response = await instance.get(`/chat/profile/suspended`);
    return response.data;
}

/* liftSuspend */

export const liftSuspend = async () => {
    const response = await instance.put(`/chat/profile/suspended/lift`);
    return response.data;
}

/* getChats */

interface IGetChatsProps {
    page: number;
    size: number;
    filter: "latest" | "trending";
    isMyTypeOnly: boolean;
}
export const getChats = async ({ page, size, filter, isMyTypeOnly }: IGetChatsProps) => {
    const response = await instance.get(`/chat`, {
        params: { page, size, filter, isMyTypeOnly }
    });
    return response.data;
}

/* getChat */

interface IGetChatProps {
    slug: string;
}
export const getChat = async ({ slug }: IGetChatProps) => {
    const response = await instance.get(`/chat/${slug}`);
    return response.data;
}

/* getMyProfile */

export const getMyProfile = async () => {
    const response = await instance.get(`/chat/profile/me`);
    return response.data;
}

/* getMyProfileChats */

interface IGetMyProfileChatsProps {
    page: number;
    size: number;
}
export const getMyProfileChats = async ({ page, size }: IGetMyProfileChatsProps) => {
    const response = await instance.get(`/chat/profile/me/chats`, {
        params: { page, size }
    });
    return response.data;
}

/* getMyProfileCommentedOnChats */

interface IGetMyProfileCommentedOnChatsProps {
    page: number;
    size: number;
}
export const getMyProfileCommentedOnChats = async ({ page, size }: IGetMyProfileCommentedOnChatsProps) => {
    const response = await instance.get(`/chat/profile/me/commented-on-chats`, {
        params: { page, size }
    });
    return response.data;
}

/* getMyProfileSavedChats */

interface IGetMyProfileSavedChatsProps {
    page: number;
    size: number;
}
export const getMyProfileSavedChats = async ({ page, size }: IGetMyProfileSavedChatsProps) => {
    const response = await instance.get(`/chat/profile/me/saved-chats`, {
        params: { page, size }
    });
    return response.data;
}

/* getProfile */

interface IGetProfileProps {
    nickname: string;
}
export const getProfile = async ({ nickname }: IGetProfileProps) => {
    const response = await instance.get(`/chat/profile/${nickname}`);
    return response.data;
}

/* getProfileChats */

interface IGetProfileChatsProps {
    nickname: string;
    page: number;
    size: number;
}
export const getProfileChats = async ({ nickname, page, size }: IGetProfileChatsProps) => {
    const response = await instance.get(`/chat/profile/${nickname}/chats`, {
        params: { page, size }
    });
    return response.data;
}

/* getProfileCommentedOnChats */

interface IGetProfileCommentedOnChatsProps {
    nickname: string;
    page: number;
    size: number;
}

export const getProfileCommentedOnChats = async ({ nickname, page, size }: IGetProfileCommentedOnChatsProps) => {
    const response = await instance.get(`/chat/profile/${nickname}/commented-on-chats`, {
        params: { page, size }
    });
    return response.data;
}

/* checkNicknameDuplicate */

interface ICheckNicknameDuplicateProps {
    nickname: string;
}
export const checkNicknameDuplicate = async ({ nickname }: ICheckNicknameDuplicateProps) => {
    const response = await instance.get(`/chat/profile/check-nickname-duplicate`, {
        params: { nickname }
    });
    return response.data;
}

/* updateProfile */

export interface IUpdateProfilePayload {
    avatar?: string | null;
    nickname?: string;
    bio?: string;
    visibility?: "PUBLIC" | "PRIVATE";
}

export const updateProfile = async (payload: IUpdateProfilePayload) => {
    const response = await instance.put(`/chat/profile`, payload);
    return response.data;
}

/* getMyLikes */

interface IGetMyLikesProps {
    page: number;
    size: number;
}

export const getMyLikes = async ({ page, size }: IGetMyLikesProps) => {
    const response = await instance.get(`/chat/profile/likes`, {
        params: { page, size }
    });
    return response.data;
}

/* getChatLikes */

interface IGetChatLikesProps {
    chatId: string;
    page: number;
    size: number;
}
export const getChatLikes = async ({ chatId, page, size }: IGetChatLikesProps) => {
    const response = await instance.get(`/chat/${chatId}/likes`, {
        params: { page, size }
    });
    return response.data;
}

/* getCommentLikes */

interface IGetCommentLikesProps {
    commentId: string;
    page: number;
    size: number;
}
export const getCommentLikes = async ({ commentId, page, size }: IGetCommentLikesProps) => {
    const response = await instance.get(`/chat/comment/${commentId}/likes`, {
        params: { page, size }
    });
    return response.data;
}

/* getReplyLikes */

interface IGetReplyLikesProps {
    replyId: string;
    page: number;
    size: number;
}
export const getReplyLikes = async ({ replyId, page, size }: IGetReplyLikesProps) => {
    const response = await instance.get(`/chat/reply/${replyId}/likes`, {
        params: { page, size }
    });
    return response.data;
}

/* getBlockingProfiles */

interface IGetBlockingProfilesProps {
    page: number;
    size: number;
}

export const getBlockingProfiles = async ({ page, size }: IGetBlockingProfilesProps) => {
    const response = await instance.get(`/chat/profile/blocking`, {
        params: { page, size }
    });
    return response.data;
}

/* createChat */

interface ICreateChatProps {
    content: string;
    imageUrls: string[];
    isMyTypeOnly: boolean;
}
export const createChat = async ({ content, imageUrls, isMyTypeOnly }: ICreateChatProps) => {
    const response = await instance.post(`/chat`, { content, imageUrls, isMyTypeOnly });
    return response.data;
}

/* createComment */

interface ICreateCommentProps {
    slug: string;
    content: string;
}
export const createComment = async ({ slug, content }: ICreateCommentProps) => {
    const response = await instance.post(`/chat/${slug}/comment`, { content });
    return response.data;
}

/* createReply */

interface ICreateReplyProps {
    slug: string;
    commentId: string;
    content: string;
}
export const createReply = async ({ slug, commentId, content }: ICreateReplyProps) => {
    const response = await instance.post(`/chat/${slug}/comment/${commentId}/reply`, { content });
    return response.data;
}

/* toggleLikeChat */

interface IToggleLikeChatProps {
    slug: string;
}
export const toggleLikeChat = async ({ slug }: IToggleLikeChatProps) => {
    const response = await instance.post(`/chat/${slug}/like`);
    return response.data;
}

/* toggleSaveChat */

interface IToggleSaveChatProps {
    slug: string;
}
export const toggleSaveChat = async ({ slug }: IToggleSaveChatProps) => {
    const response = await instance.post(`/chat/${slug}/save`);
    return response.data;
}

/* toggleLikeComment */

interface IToggleLikeCommentProps {
    slug: string;
    commentId: string;
}
export const toggleLikeComment = async ({ slug, commentId }: IToggleLikeCommentProps) => {
    const response = await instance.post(`/chat/${slug}/comment/${commentId}/like`);
    return response.data;
}

/* deleteComment */

interface IDeleteCommentProps {
    slug: string;
    commentId: string;
}
export const deleteComment = async ({ slug, commentId }: IDeleteCommentProps) => {
    const response = await instance.delete(`/chat/${slug}/comment/${commentId}`);
    return response.data;
}

/* toggleReportComment */

interface IToggleReportCommentProps {
    slug: string;
    commentId: string;
}
export const toggleReportComment = async ({ slug, commentId }: IToggleReportCommentProps) => {
    const response = await instance.post(`/chat/${slug}/comment/${commentId}/report`);
    return response.data;
}

/* toggleLikeReply */

interface IToggleLikeReplyProps {
    slug: string;
    commentId: string;
    replyId: string;
}
export const toggleLikeReply = async ({ slug, commentId, replyId }: IToggleLikeReplyProps) => {
    const response = await instance.post(`/chat/${slug}/comment/${commentId}/reply/${replyId}/like`);
    return response.data;
}

/* deleteReply */

interface IDeleteReplyProps {
    slug: string;
    commentId: string;
    replyId: string;
}
export const deleteReply = async ({ slug, commentId, replyId }: IDeleteReplyProps) => {
    const response = await instance.delete(`/chat/${slug}/comment/${commentId}/reply/${replyId}`);
    return response.data;
}

/* toggleReportReply */

interface IToggleReportReplyProps {
    slug: string;
    commentId: string;
    replyId: string;
}
export const toggleReportReply = async ({ slug, commentId, replyId }: IToggleReportReplyProps) => {
    const response = await instance.post(`/chat/${slug}/comment/${commentId}/reply/${replyId}/report`);
    return response.data;
}

/* deleteChat */

interface IDeleteChatProps {
    slug: string;
}
export const deleteChat = async ({ slug }: IDeleteChatProps) => {
    const response = await instance.delete(`/chat/${slug}`);
    return response.data;
}

/* toggleReportChat */

interface IToggleReportChatProps {
    slug: string;
}
export const toggleReportChat = async ({ slug }: IToggleReportChatProps) => {
    const response = await instance.post(`/chat/${slug}/report`);
    return response.data;
}

/* toggleBlockProfile */

interface IToggleBlockProfileProps {
    nickname: string;
}
export const toggleBlockProfile = async ({ nickname }: IToggleBlockProfileProps) => {
    const response = await instance.post(`/chat/profile/${nickname}/block`);
    return response.data;
}





/* ------------------------------------------------------------------ */















/* upload url for chat */

interface IGetUploadUrlForChatProps {
    profileId: string;
    fileName: string;
    fileMimeType: string;
}
export const getUploadUrlForChat = async ({ profileId, fileName, fileMimeType }: IGetUploadUrlForChatProps) => {
    const response = await instance.get(`/get-upload-url-for-chat`, { params: { profileId, fileName, fileMimeType } });
    return response.data;
}

/* upload file for chat */

interface IUploadFileForChatProps {
    profileId: string;
    file: File;
}

export const uploadFileForChat = async ({ profileId, file }: IUploadFileForChatProps) => {
    try {

        ///////////////////////////////////////////////////////////////////////// 업로드 유효성 검사

        if (!file) {
            throw new Error('파일 정보가 올바르지 않습니다.');
        }

        /////////////////////////////////////////////////////////////////////////

        const fileName = file.name;
        const fileMimeType = file.type;

        ///////////////////////////////////////////////////////////////////////// get upload url

        const { awsUploadUrl, awsFileUrl } = await getUploadUrlForChat({ profileId, fileName, fileMimeType });

        ///////////////////////////////////////////////////////////////////////// upload file to aws

        const fileUrl = await uploadFileToAws({ file, fileMimeType, awsUploadUrl, awsFileUrl });

        ///////////////////////////////////////////////////////////////////////// 업로드 성공

        return fileUrl;

    } catch (error) {
        console.error(error);
        throw error;
    }
}

/* upload files for chat */

interface IUploadFilesForChatProps {
    profileId: string;
    files: File[];
}

export const uploadFilesForChat = async ({ profileId, files }: IUploadFilesForChatProps) => {

    ///////////////////////////////////////////////////////////////////////// 업로드 유효성 검사

    if (!files?.length) return [];

    ///////////////////////////////////////////////////////////////////////// uploaded urls

    const uploadedUrls: string[] = [];

    ///////////////////////////////////////////////////////////////////////// upload files

    for (const file of files) {
        const url = await uploadFileForChat({ profileId, file });
        uploadedUrls.push(url);
    }
    
    return uploadedUrls;
};

/* upload url for avatar */

interface IGetUploadUrlForAvatarProps {
    profileId: string;
    fileName: string;
    fileMimeType: string;
}
export const getUploadUrlForAvatar = async ({ profileId, fileName, fileMimeType }: IGetUploadUrlForAvatarProps) => {
    const response = await instance.get(`/get-upload-url-for-avatar`, { params: { profileId, fileName, fileMimeType } });
    return response.data;
}

/* upload file for avatar */

interface IUploadFileForAvatarProps {
    profileId: string;
    file: File;
}

export const uploadFileForAvatar = async ({ profileId, file }: IUploadFileForAvatarProps) => {
    try {

        ///////////////////////////////////////////////////////////////////////// 업로드 유효성 검사

        if (!file) {
            throw new Error('파일 정보가 올바르지 않습니다.');
        }

        /////////////////////////////////////////////////////////////////////////

        const fileName = file.name;
        const fileMimeType = file.type;

        ///////////////////////////////////////////////////////////////////////// get upload url

        const { awsUploadUrl, awsFileUrl } = await getUploadUrlForAvatar({ profileId, fileName, fileMimeType });

        ///////////////////////////////////////////////////////////////////////// upload file to aws

        const fileUrl = await uploadFileToAws({ file, fileMimeType, awsUploadUrl, awsFileUrl });

        ///////////////////////////////////////////////////////////////////////// 업로드 성공

        return fileUrl;

    } catch (error) {
        console.error(error);
        throw error;
    }
}


/* upload url for company */

interface IGetUploadUrlForCompanyProps {
    companyId: string;
    fileName: string;
    fileMimeType: string;
}
export const getUploadUrlForCompany = async ({ companyId, fileName, fileMimeType }: IGetUploadUrlForCompanyProps) => {
    const response = await instance.get(`/get-upload-url-for-company`, { params: { companyId, fileName, fileMimeType } });
    return response.data;
}

/* upload file for company */

interface IUploadFileForCompanyProps {
    companyId: string;
    file: File;
}

export const uploadFileForCompany = async ({ companyId, file }: IUploadFileForCompanyProps) => {
    try {

        ///////////////////////////////////////////////////////////////////////// 업로드 유효성 검사

        if (!file) {
            throw new Error('파일 정보가 올바르지 않습니다.');
        }

        /////////////////////////////////////////////////////////////////////////

        const fileName = file.name;
        const fileMimeType = file.type;

        ///////////////////////////////////////////////////////////////////////// get upload url

        const { awsUploadUrl, awsFileUrl } = await getUploadUrlForCompany({ companyId, fileName, fileMimeType });

        ///////////////////////////////////////////////////////////////////////// upload file to aws

        const fileUrl = await uploadFileToAws({ file, fileMimeType, awsUploadUrl, awsFileUrl });

        ///////////////////////////////////////////////////////////////////////// 업로드 성공

        return fileUrl;

    } catch (error) {
        console.error(error);
        throw error;
    }
}















/* upload Url */

interface IGetUploadUrlProps {
    fileName: string;
    fileMimeType: string;
}

export const getUploadUrl = async ({ fileName, fileMimeType }: IGetUploadUrlProps) => {
    const response = await instance.get('/get-upload-url', {
        params: {
            fileName,
            fileMimeType
        }
    });
    return response.data;
}

/* upload file */

interface IUploadFileToAwsProps {
    file: File;
    fileMimeType: string;
    awsUploadUrl: string;
    awsFileUrl: string;
}

export const uploadFileToAws = async ({ file, fileMimeType, awsUploadUrl, awsFileUrl }: IUploadFileToAwsProps) => {
    try {
        
        console.log('Uploading to URL:', awsUploadUrl);
        console.log('File MIME Type:', fileMimeType);

        ///////////////////////////////////////////////////////////////////////// 이미지 압축

        const compressedImageBlob = await compressImage(file);

        ///////////////////////////////////////////////////////////////////////// 파일 크기 초과 검사

        const MAX_SIZE = 10 * 1024 * 1024;

        if (compressedImageBlob.size > MAX_SIZE) {
            throw new Error('파일 크기는 10MB 이하로 업로드해주세요.');
        }

        ///////////////////////////////////////////////////////////////////////// PUT 요청 전

        const uploadResponse = await fetch(awsUploadUrl, {
            method: 'PUT',
            headers: {
                'Content-Type': fileMimeType,
                'Accept': 'application/json'
            },
            body: compressedImageBlob,
        });

        ///////////////////////////////////////////////////////////////////////// 업로드 성공

        if (uploadResponse.ok) {

            ///////////////////////////////////////////////////////////////////////// awsFileUrl 반환

            return awsFileUrl;
        } else {
            const errorText = await uploadResponse.text();
            console.error('Upload failed with status:', uploadResponse.status);
            console.error('Error response:', errorText);
            throw new Error(`Upload failed: ${uploadResponse.status} - ${errorText}`);
        }

    } catch (error) {
        console.error('uploadFileToAws 에러:', error);
        throw error;
    }
}

/* upload file */

interface IUploadFileProps {
    file: File;
}

export const uploadFile = async ({ file }: IUploadFileProps) => {
    try {

        ///////////////////////////////////////////////////////////////////////// 업로드 유효성 검사

        if (!file) {
            throw new Error('파일 정보가 올바르지 않습니다.');
        }

        /////////////////////////////////////////////////////////////////////////

        const fileName = file.name;
        const fileMimeType = file.type;

        ///////////////////////////////////////////////////////////////////////// get upload url

        const { awsUploadUrl, awsFileUrl } = await getUploadUrl({ fileName, fileMimeType });

        ///////////////////////////////////////////////////////////////////////// upload file to aws

        const fileUrl = await uploadFileToAws({ file, fileMimeType, awsUploadUrl, awsFileUrl });

        ///////////////////////////////////////////////////////////////////////// 업로드 성공

        return fileUrl;

    } catch (error) {
        console.error(error);
        throw error;
    }
}

/* upload files */

interface IUploadFilesProps {
    type: string;
    id: string;
    files: File[];
}

export const uploadFiles = async ({ type, id, files }: IUploadFilesProps) => {

    ///////////////////////////////////////////////////////////////////////// 업로드 유효성 검사

    if (!files?.length) return [];

    ///////////////////////////////////////////////////////////////////////// uploaded urls

    const uploadedUrls: string[] = [];

    ///////////////////////////////////////////////////////////////////////// upload files

    for (const file of files) {
        const url = await uploadFileWithId({ type, id, file });
        uploadedUrls.push(url);
    }
    
    return uploadedUrls;
};

/* get medicine */

interface IGetMedicineProps {
    medicineId:string;
}
export const getMedicine = async ({ medicineId }:IGetMedicineProps) => {
    const response = await instance.get(`/medicine/${medicineId}`);
    return response.data;
}

/* get medicine trades */

interface IGetMedicineTradeListProps {
    medicineId:string;
    page:number;
    size:number;
}
export const getMedicineTradeList = async ({ medicineId, page, size }:IGetMedicineTradeListProps) => {
    const response = await instance.get(`/medicine/${medicineId}/trades`, {
        params: {
            page,
            size
        }
    });
    return response.data;
}

/* get similar medicine */

interface IGetSimilarMedicineListProps {
    medicineId:string;
}
export const getSimilarMedicineList = async ({ medicineId }:IGetSimilarMedicineListProps) => {
    const response = await instance.get(`/medicine/${medicineId}/similar`);
    return response.data;
}









/* get Medicines by keywords */

interface IGetMedicinesByKeywordsProps {
    medicineId: string;
    keywords: string[];

    page: number;
    size: number;
}
export const getMedicinesByKeywords = async ({ medicineId, keywords, page, size }:IGetMedicinesByKeywordsProps) => {
    const response = await instance.get(`/medicine/${medicineId}/by-keywords`, {
        params: {
            keywords,
            page,
            size
        }
    });
    return response.data;
}

/* create click log */

interface ICreateClickLogProps {
    type: "SIMILAR_MODAL" | "INGREDIENT_GROUP" | "KEYWORD" | "MALL";
    surface?: "SEARCH" | "MEDICINE";

    searchKeyword?: string;
    medicineId?: string;
    keywordId?: string;
    keyword?: string;
}

// 로그 저장이 실패해도 클릭 동작은 그대로 진행한다
export const createClickLog = async ({ type, surface, searchKeyword, medicineId, keywordId, keyword }: ICreateClickLogProps) => {
    try {
        const response = await instance.post("/user/click-log", {
            type,
            surface,
            searchKeyword,
            medicineId,
            keywordId,
            keyword,
        });
        return response.data;
    } catch {
        return null;
    }
}

/* create medicine entry log */

interface ICreateMedicineEntryLogProps {
    source: "SEARCH_LIST" | "SIMILAR_IDENTICAL" | "SIMILAR_RECOMMEND" | "KEYWORD_LIST";
    surface: "SEARCH" | "MEDICINE";

    medicineId: string;

    searchKeyword?: string;
    fromMedicineId?: string;
    keywords?: string[];
}

// 로그 저장이 실패해도 상세 페이지 이동은 그대로 진행한다
export const createMedicineEntryLog = async ({ source, surface, medicineId, searchKeyword, fromMedicineId, keywords }: ICreateMedicineEntryLogProps) => {
    try {
        const response = await instance.post("/user/medicine-entry-log", {
            source,
            surface,
            medicineId,
            searchKeyword,
            fromMedicineId,
            keywords,
        });
        return response.data;
    } catch {
        return null;
    }
}













/* ------------------------------------------------ */




















/* ------------------------------------------------ */

/* adminLogIn */

interface IAdminLogInProps {
    email:string;
    password:string;
}

export const adminLogIn = async ({ email, password }:IAdminLogInProps) => {
    const response = await instance.post("/admin/login", {
        email,
        password
    });
    return response.data;
}

/* admin get dashboard */

export const adminGetDashboard = async () => {
    const response = await instance.get("/admin/dashboard");
    return response.data;
}

/* admin get users */

interface IAdminGetUsersProps {
    page:number;
    size:number;
    keyword:string | null;
    status:string;
}

export const adminGetUsers = async ({ page, size, keyword, status }:IAdminGetUsersProps) => {
    const response = await instance.get("/admin/users", {
        params: {
            page,
            size,
            keyword,
            status
        }
    });
    return response.data;
}

/* admin update certificate status */

interface IAdminUpdateCertificateStatusProps {
    userId:string;
    certificateId:string;
    status:string;
    rejectedReason:string | null;
    imageIsValid:boolean;
}

/* status, rejectedReason, *IsValid 를 한 요청에 같이 보낸다. */
export const adminUpdateCertificateStatus = async ({
    userId,
    certificateId,
    status,
    rejectedReason,
    imageIsValid,
}:IAdminUpdateCertificateStatusProps) => {
    const response = await instance.put(`/admin/users/${userId}/certificates/${certificateId}/status`, {
        status,
        rejectedReason,
        imageIsValid,
    });
    return response.data;
}

/* admin update pharmacy status */

interface IAdminUpdatePharmacyStatusProps {
    userId:string;
    pharmacyId:string;
    status:string;
    rejectedReason:string | null;
    bizRegNoIsValid:boolean;
    businessRegistrationCertImageIsValid:boolean;
    establishmentRegistrationCertImageIsValid:boolean;
    healthcareInstitutionIdIsValid:boolean;
}

export const adminUpdatePharmacyStatus = async ({
    userId,
    pharmacyId,
    status,
    rejectedReason,
    bizRegNoIsValid,
    businessRegistrationCertImageIsValid,
    establishmentRegistrationCertImageIsValid,
    healthcareInstitutionIdIsValid,
}:IAdminUpdatePharmacyStatusProps) => {
    /* status + 각 서류/번호 isValid 를 한 번에 저장 */
    const response = await instance.put(`/admin/users/${userId}/pharmacies/${pharmacyId}/status`, {
        status,
        rejectedReason,
        bizRegNoIsValid,
        businessRegistrationCertImageIsValid,
        establishmentRegistrationCertImageIsValid,
        healthcareInstitutionIdIsValid,
    });
    return response.data;
}

/* admin update license status */

interface IAdminUpdateLicenseStatusProps {
    userId:string;
    licenseId:string;
    status:string;
    rejectedReason:string | null;
    imageIsValid:boolean;
    numberIsValid:boolean;
}

export const adminUpdateLicenseStatus = async ({
    userId,
    licenseId,
    status,
    rejectedReason,
    imageIsValid,
    numberIsValid,
}:IAdminUpdateLicenseStatusProps) => {
    /* status + 면허증/면허번호 isValid 를 한 번에 저장 */
    const response = await instance.put(`/admin/users/${userId}/licenses/${licenseId}/status`, {
        status,
        rejectedReason,
        imageIsValid,
        numberIsValid,
    });
    return response.data;
}

/* admin get owner users */

interface IAdminGetOwnerUsersProps {
    page:number;
    size:number;
    keyword:string | null;
    status:string;
}

export const adminGetOwnerUsers = async ({ page, size, keyword, status }:IAdminGetOwnerUsersProps) => {
    const response = await instance.get("/admin/users/owner", {
        params: {
            page,
            size,
            keyword,
            status
        }
    });
    return response.data;
}

/* admin get pharmacist users */

interface IAdminGetPharmacistUsersProps {
    page:number;
    size:number;
    keyword:string | null;
    status:string;
}

export const adminGetPharmacistUsers = async ({ page, size, keyword, status }:IAdminGetPharmacistUsersProps) => {
    const response = await instance.get("/admin/users/pharmacist", {
        params: {
            page,
            size,
            keyword,
            status
        }
    });
    return response.data;
}

/* admin get student users */

interface IAdminGetStudentUsersProps {
    page:number;
    size:number;
    keyword:string | null;
    status:string;
}

export const adminGetStudentUsers = async ({ page, size, keyword, status }:IAdminGetStudentUsersProps) => {
    const response = await instance.get("/admin/users/student", {
        params: {
            page,
            size,
            keyword,
            status
        }
    });
    return response.data;
}

/* admin get guest users */

interface IAdminGetGuestUsersProps {
    page:number;
    size:number;
    keyword:string | null;
}

export const adminGetGuestUsers = async ({ page, size, keyword }:IAdminGetGuestUsersProps) => {
    const response = await instance.get("/admin/users/guest", {
        params: {
            page,
            size,
            keyword
        }
    });
    return response.data;
}

/* admin get pharmacies */

interface IAdminGetPharmaciesProps {
    page:number;
    size:number;
    keyword:string | null;
}

export const adminGetPharmacies = async ({ page, size, keyword }:IAdminGetPharmaciesProps) => {
    const response = await instance.get("/admin/pharmacies", {
        params: {
            page,
            size,
            keyword
        }
    });
    return response.data;
}

/* admin get trades */

interface IAdminGetTradesProps {
    page:number;
    size:number;
    keyword:string | null;
}

export const adminGetTrades = async ({ page, size, keyword }:IAdminGetTradesProps) => {
    const response = await instance.get("/admin/trades", {
        params: {
            page,
            size,
            keyword
        }
    });
    return response.data;
}

/* admin get certs */

interface IAdminGetCertsProps {
    page:number;
    size:number;
    keyword:string | null;
}

export const adminGetCerts = async ({ page, size, keyword }:IAdminGetCertsProps) => {
    const response = await instance.get("/admin/certs", {
        params: {
            page,
            size,
            keyword
        }
    });
    return response.data;
}

/* admin get companies */

interface IAdminGetCompaniesProps {
    page:number;
    size:number;
    keyword:string | null;
}

export const adminGetCompanies = async ({ page, size, keyword }:IAdminGetCompaniesProps) => {
    const response = await instance.get("/admin/companies", {
        params: {
            page,
            size,
            keyword
        }
    });
    return response.data;
}

/* admin get company medicines */

interface IAdminGetCompanyMedicinesProps {
    companyId:string;
}
export const adminGetCompanyMedicines = async ({ companyId }:IAdminGetCompanyMedicinesProps) => {
    const response = await instance.get(`/admin/companies/${companyId}/medicines`);
    return response.data;
}

/* admin update company image */

interface IAdminUpdateCompanyImageProps {
    companyId:string;
    imageUrl:string;
}

export const adminUpdateCompanyImage = async ({ companyId, imageUrl }:IAdminUpdateCompanyImageProps) => {
    const response = await instance.put(`/admin/companies/${companyId}/image`, { imageUrl });
    return response.data;
}

/* admin update company */

interface IAdminUpdateCompanyContactProps {
    companyId:string;
    contact:string;
}
export const adminUpdateCompanyContact = async ({ companyId, contact }:IAdminUpdateCompanyContactProps) => {
    const response = await instance.put(`/admin/companies/${companyId}/contact`, { contact });
    return response.data;
}

/* admin update company homepage */

interface IAdminUpdateCompanyHomepageProps {
    companyId:string;
    homepage:string;
}
export const adminUpdateCompanyHomepage = async ({ companyId, homepage }:IAdminUpdateCompanyHomepageProps) => {
    const response = await instance.put(`/admin/companies/${companyId}/homepage`, { homepage });
    return response.data;
}

/* admin update company mall */

interface IAdminUpdateCompanyMallProps {
    companyId:string;
    mall:string;
}
export const adminUpdateCompanyMall = async ({ companyId, mall }:IAdminUpdateCompanyMallProps) => {
    const response = await instance.put(`/admin/companies/${companyId}/mall`, { mall });
    return response.data;
}

/* adminUpdateCompanyBizRegNo */

interface IAdminUpdateCompanyBizRegNoProps {
    companyId:string;
    bizRegNo:string;
}
export const adminUpdateCompanyBizRegNo = async ({ companyId, bizRegNo }:IAdminUpdateCompanyBizRegNoProps) => {
    const response = await instance.put(`/admin/companies/${companyId}/biz-reg-no`, { bizRegNo });
    return response.data;
}

/* admin get medicines */

interface IAdminGetMedicinesProps {
    page:number;
    size:number;
    isDisplay:string;
    representativeCode:string;
    standardCode:string;
    seqNo:string;
    nameKor:string;
    nameEng:string;
    category:string;
    maker:string;
    seller:string;
    dosageRoute:string;
    packageUnit:string;
    packageQuantity:string;
    packageForm:string;
    packageType:string;
    basis:string;
    ingredientInfo:string;
    ingredientEng:string;
    packImage:string;
    pillImage:string;
    ingredientCode:string;
    atcCode:string;
    classification:string;
    basePrice:string;
    efficacy:string;
    note:string;
    keyword:string;
    searchKeyword:string;
}

export const adminGetMedicines = async ({ page,
                                          size,
                                         representativeCode,
                                         standardCode,
                                         seqNo,
                                         nameKor,
                                         nameEng,
                                         category,
                                         maker,
                                         seller,
                                         dosageRoute,
                                         packageUnit,
                                         packageQuantity,
                                         packageForm,
                                         packageType,
                                         basis,
                                         ingredientInfo,
                                         ingredientEng,
                                         packImage,
                                         pillImage,
                                         ingredientCode,
                                         atcCode,
                                         classification,
                                         basePrice,
                                         efficacy,
                                         note,
                                         keyword,
                                          isDisplay,
                                          searchKeyword }:IAdminGetMedicinesProps) => {
    const response = await instance.get("/admin/medicines", {
        params: {
            page,
            size,
            representativeCode,
            standardCode,
            seqNo,
            nameKor,
            nameEng,
            category,
            maker,
            seller,
            dosageRoute,
            packageUnit,
            packageQuantity,
            packageForm,
            packageType,
            basis,
            ingredientInfo,
            ingredientEng,
            packImage,
            pillImage,
            ingredientCode,
            atcCode,
            classification,
            basePrice,
            efficacy,
            note,
            isDisplay,
            keyword,
            searchKeyword
        }
    });
    return response.data;
}


/* admin get advices */

interface IAdminGetAdvicesProps {
    page:number;
    size:number;
}
export const adminGetAdvices = async ({ page, size }:IAdminGetAdvicesProps) => {
    const response = await instance.get("/admin/advices", {
        params: {
            page,
            size
        }
    });
    return response.data;
}

/* admin update user status */

interface IAdminUpdateUserStatusProps {
    userId:string;
    status:string;
}

export const adminUpdateUserStatus = async ({ userId, status }:IAdminUpdateUserStatusProps) => {
    const response = await instance.put(`/admin/users/${userId}/status`, { status });
    return response.data;
}

/* admin update user validity */

interface IAdminUpdateUserValidityProps {
    userId:string;
    key:string;
    isValid:boolean;
}
export const adminUpdateUserValidity = async ({ userId, key, isValid }:IAdminUpdateUserValidityProps) => {
    const response = await instance.put(`/admin/users/${userId}/validity`, { key, isValid });
    return response.data;
}

/* admin update user field */

interface IAdminUpdateUserRejectedReasonProps {
    userId: string;
    rejectedReason: string;
}
export const adminUpdateUserRejectedReason = async ({ userId, rejectedReason }: IAdminUpdateUserRejectedReasonProps) => {
    const response = await instance.put(`/admin/users/${userId}/rejected-reason`, { rejectedReason });
    return response.data;
}

/* admin update pharmacy validity */

interface IAdminUpdatePharmacyValidityProps {
    pharmacyId:string;
    key:string;
    isValid:boolean;
}
export const adminUpdatePharmacyValidity = async ({ pharmacyId, key, isValid }:IAdminUpdatePharmacyValidityProps) => {
    const response = await instance.put(`/admin/pharmacies/${pharmacyId}/validity`, { key, isValid });
    return response.data;
}

/* admin create medicine */

interface IAdminCreateMedicineProps {
    medicine: ICreateMedForm;             
}

export const adminCreateMedicine = async ({ medicine }:IAdminCreateMedicineProps) => {
    const response = await instance.post("/admin/medicines", { medicine });
    return response.data;
}

/* admin delete users */

interface IAdminDeleteUsersProps {
    userIds: string[];
}
export const adminDeleteUsers = async ({ userIds }:IAdminDeleteUsersProps) => {
    const response = await instance.delete("/admin/users", { data: { userIds } });
    return response.data;
}

/* admin delete medicines */

interface IAdminDeleteMedicinesProps {
    medicineIds: string[];
}
export const adminDeleteMedicines = async ({ medicineIds }:IAdminDeleteMedicinesProps) => {
    const response = await instance.delete("/admin/medicines", { data: { medicineIds } });
    return response.data;
}

/* admin update medicine */

interface IAdminUpdateMedicineProps {
    medicineId: string;
    key: string;
    value: any;
}
export const adminUpdateMedicine = async ({ medicineId, key, value }: IAdminUpdateMedicineProps) => {
    const response = await instance.put(`/admin/medicines/${medicineId}`, { key, value });
    return response.data;
}

/* admin get medicine pack image */

interface IAdminGetMedicinePackImagesProps {
    medicineId: string;
}
export const adminGetMedicinePackImages = async ({ medicineId }: IAdminGetMedicinePackImagesProps) => {
    const response = await instance.get(`/admin/medicines/${medicineId}/pack-images`);
    return response.data;
}

/* admin add medicine pack image */

/* admin update medicine pack images */

interface IAdminUpdateMedicinePackImagesProps {
    medicineId: string;
    packImages: string[];
}

export const adminUpdateMedicinePackImages = async ({ medicineId, packImages }: IAdminUpdateMedicinePackImagesProps) => {
    const response = await instance.put(`/admin/medicines/${medicineId}/pack-images`, { packImages });
    return response.data;
}

/* admin delete medicine pack images */

interface IAdminDeleteMedicinePackImageProps {
    medicineId: string;
    packImage: string;
}

export const adminDeleteMedicinePackImage = async ({ medicineId, packImage }: IAdminDeleteMedicinePackImageProps) => {
    const response = await instance.delete(`/admin/medicines/${medicineId}/pack-images`, { data: { packImage } });
    return response.data;
}

/* admin delete companies */

interface IAdminDeleteCompaniesProps {
    companyIds: string[];
}
export const adminDeleteCompanies = async ({ companyIds }: IAdminDeleteCompaniesProps) => {
    const response = await instance.delete("/admin/companies", { data: { companyIds } });
    return response.data;
}













/* upload url with id */

interface IGetUploadUrlWithIdProps {
    type: string;
    id: string;
    fileName: string;
    fileMimeType: string;
}

export const getUploadUrlWithId = async ({ type, id, fileName, fileMimeType }: IGetUploadUrlWithIdProps) => {
    const response = await instance.get(`/admin/get-upload-url-with-id`, {
        params: {
            type,
            id,
            fileName,
            fileMimeType
        }
    });
    return response.data;
}

/* upload file */

interface IUploadFileWithIdProps {
    type: string;
    id: string;
    file: File;
}

export const uploadFileWithId = async ({ type, id, file }: IUploadFileWithIdProps) => {
    try {

        ///////////////////////////////////////////////////////////////////////// 업로드 유효성 검사

        if (!file) throw new Error('파일 정보가 올바르지 않습니다.');

        /////////////////////////////////////////////////////////////////////////

        const fileName = file.name;
        const fileMimeType = file.type;

        ///////////////////////////////////////////////////////////////////////// get upload url

        const { awsUploadUrl, awsFileUrl } = await getUploadUrlWithId({ type, id, fileName, fileMimeType });

        ///////////////////////////////////////////////////////////////////////// upload file to aws

        const fileUrl = await uploadFileToAws({ file, fileMimeType, awsUploadUrl, awsFileUrl });

        ///////////////////////////////////////////////////////////////////////// 업로드 성공

        return fileUrl;

    } catch (error) {
        console.error(error);
        throw new Error('uploadFileWithId 에러');
    }
}


/* admin delete company image */

interface IAdminDeleteCompanyImageProps {
    companyId:string;
}

export const adminDeleteCompanyImage = async ({ companyId }:IAdminDeleteCompanyImageProps) => {
    const response = await instance.delete(`/admin/companies/${companyId}/image`);
    return response.data;
}

/* admin get keywords */

interface IAdminGetKeywordsProps {
    page:number;
    size:number;
    searchKeyword:string | null;
}
export const adminGetKeywords = async ({ page, size, searchKeyword }:IAdminGetKeywordsProps) => {
    const response = await instance.get("/admin/keywords", {
        params: {
            page,
            size,
            searchKeyword
        }
    });
    return response.data;
}

/* admin get keyword */

interface IAdminGetKeywordProps {
    keywordId:string;
}
export const adminGetKeyword = async ({ keywordId }:IAdminGetKeywordProps) => {
    const response = await instance.get(`/admin/keywords/${keywordId}`);
    return response.data;
}

/* admin update keyword */

interface IAdminUpdateKeywordProps {
    keywordId:string;
    modifyKeyword:string;
}
export const adminUpdateKeyword = async ({ keywordId, modifyKeyword }:IAdminUpdateKeywordProps) => {
    const response = await instance.put(`/admin/keywords/${keywordId}`, { modifyKeyword });
    return response.data;
}

/* admin create tag */

interface IAdminCreateTagProps {
    keywordId: string;
    tag: string;
}
export const adminCreateTag = async ({ keywordId, tag }:IAdminCreateTagProps) => {
    const response = await instance.post(`/admin/keywords/${keywordId}/tag`, { tag });
    return response.data;
}

/* admin delete tag */

interface IAdminDeleteTagProps {
    keywordId: string;
    tagId: string;
}
export const adminDeleteTag = async ({ keywordId, tagId }:IAdminDeleteTagProps) => {
    const response = await instance.delete(`/admin/keywords/${keywordId}/tag/${tagId}`);
    return response.data;
}

/* admin delete keyword */

interface IAdminDeleteKeywordsProps {
    keywordIds: string[];
}
export const adminDeleteKeywords = async ({ keywordIds }:IAdminDeleteKeywordsProps) => {
    const response = await instance.delete("/admin/keywords", { data: { keywordIds } });
    return response.data;
}

/* admin create keyword */

interface IAdminCreateKeywordProps {
    keyword: string;
}
export const adminCreateKeyword = async ({ keyword }:IAdminCreateKeywordProps) => {
    const response = await instance.post("/admin/keywords", { keyword });
    return response.data;
}

/* admin get medicines by keyword */

interface IAdminGetMedicinesByKeywordProps {
    page:number;
    size:number;
    isDisplay:string;
    searchKeyword:string | null;
}
export const adminGetMedicinesByKeyword = async ({ page, size, isDisplay, searchKeyword }:IAdminGetMedicinesByKeywordProps) => {
    const response = await instance.get("/admin/medicines/by-keyword", {
        params: {
            page,
            size,
            isDisplay,
            searchKeyword
        }
    });
    return response.data;
}

/* admin auto complete keyword */

interface IAdminAutoCompleteKeywordProps {
    medicineId: string;
    searchKeyword: string;
}
export const adminAutoCompleteKeyword = async ({ medicineId, searchKeyword }:IAdminAutoCompleteKeywordProps) => {
    const response = await instance.get(`/admin/medicines/${medicineId}/by-keyword/auto-complete`, {
        params: { searchKeyword }
    });
    return response.data;
}

/* admin get medicine by keyword */

interface IAdminGetMedicineByKeywordProps {
    medicineId: string;
}
export const adminGetMedicineByKeyword = async ({ medicineId }:IAdminGetMedicineByKeywordProps) => {
    const response = await instance.get(`/admin/medicines/${medicineId}/by-keyword`);
    return response.data;
}

/* admin get Connection */

interface IAdminGetConnectionProps {
    medicineId: string;
    keywordId: string;
}
export const adminGetConnection = async ({ medicineId, keywordId }:IAdminGetConnectionProps) => {
    const response = await instance.get(`/admin/connections/${medicineId}/${keywordId}`);
    return response.data;
}

/* admin update connection */

interface IAdminUpdateConnectionProps {
    medicineId: string;
    keywordId: string;
    weight: number;
}
export const adminUpdateConnection = async ({ medicineId, keywordId, weight }:IAdminUpdateConnectionProps) => {
    const response = await instance.put(`/admin/connections/${medicineId}/${keywordId}`, { weight });
    return response.data;
}

/* admin create connection by seqNo or ingredientCode */

interface IAdminCreateConnectionBySeqNoOrIngredientCodeProps {
    seqNo: string;
    ingredientCode: string;
    keyword: string;
    weight: number;
}
export const adminCreateConnectionBySeqNoOrIngredientCode = async ({ seqNo, ingredientCode, keyword, weight }:IAdminCreateConnectionBySeqNoOrIngredientCodeProps) => {
    const response = await instance.post(`/admin/connections/by-seq-no-or-ingredient-code/${seqNo}/${ingredientCode}`, { keyword, weight });
    return response.data;
}

/* admin create connection by seqNo */

interface IAdminCreateConnectionBySeqNoProps {
    seqNo: string;
    keyword: string;
    weight: number;
}
export const adminCreateConnectionBySeqNo = async ({ seqNo, keyword, weight }:IAdminCreateConnectionBySeqNoProps) => {
    const response = await instance.post(`/admin/connections/by-seq-no/${seqNo}`, { keyword, weight });
    return response.data;
}

/* admin create connection by ingredientCode */

interface IAdminCreateConnectionByIngredientCodeProps {
    ingredientCode: string;
    keyword: string;
    weight: number;
}
export const adminCreateConnectionByIngredientCode = async ({ ingredientCode, keyword, weight }:IAdminCreateConnectionByIngredientCodeProps) => {
    const response = await instance.post(`/admin/connections/by-ingredient-code/${ingredientCode}`, { keyword, weight });
    return response.data;
}

/* admin delete connection */

interface IAdminDeleteConnectionProps {
    medicineId: string;
    keywordId: string;
}
export const adminDeleteConnection = async ({ medicineId, keywordId }:IAdminDeleteConnectionProps) => {
    const response = await instance.delete(`/admin/connections/${medicineId}/${keywordId}`);
    return response.data;
}

/* admin delete connection by seqNo or ingredientCode */

interface IAdminDeleteConnectionBySeqNoOrIngredientCodeProps {
    seqNo: string;
    ingredientCode: string;
    keywordId: string;
}
export const adminDeleteConnectionBySeqNoOrIngredientCode = async ({ seqNo, ingredientCode, keywordId }:IAdminDeleteConnectionBySeqNoOrIngredientCodeProps) => {
    const response = await instance.delete(`/admin/connections/by-seq-no-or-ingredient-code/${seqNo}/${ingredientCode}/${keywordId}`);
    return response.data;
}

/* admin delete connection by seqNo */

interface IAdminDeleteConnectionBySeqNoProps {
    seqNo: string;
    keywordId: string;
}
export const adminDeleteConnectionBySeqNo = async ({ seqNo, keywordId }:IAdminDeleteConnectionBySeqNoProps) => {
    const response = await instance.delete(`/admin/connections/by-seq-no/${seqNo}/${keywordId}`);
    return response.data;
}

/* admin delete connection by ingredientCode */

interface IAdminDeleteConnectionByIngredientCodeProps {
    ingredientCode: string;
    keywordId: string;
}
export const adminDeleteConnectionByIngredientCode = async ({ ingredientCode, keywordId }:IAdminDeleteConnectionByIngredientCodeProps) => {
    const response = await instance.delete(`/admin/connections/by-ingredient-code/${ingredientCode}/${keywordId}`);
    return response.data;
}

/* admin create connection */

interface IAdminCreateConnectionProps {
    medicineId: string;
    keyword: string;
    weight: number;
}
export const adminCreateConnection = async ({ medicineId, keyword, weight }:IAdminCreateConnectionProps) => {
    const response = await instance.post(`/admin/connections/${medicineId}`, { keyword, weight });
    return response.data;
}


/* ------------------------------------------------ meilisearch ------------------------------------------------ */

/* admin meilisearch full setup */

export const adminMeilisearchFullSetup = async () => {
    const response = await instance.post(`/admin/meilisearch/setup`);
    return response.data;
}

/* admin meilisearch update settings */

export const adminMeilisearchUpdateSettings = async () => {
    const response = await instance.post(`/admin/meilisearch/update-settings`);
    return response.data;
}

/* admin meilisearch reIndex */

export const adminMeilisearchReIndex = async () => {
    const response = await instance.post(`/admin/meilisearch/re-index`);
    return response.data;
}

/* admin meilisearch auto-complete setup */

export const adminMeilisearchAutoCompleteSetup = async () => {
    const response = await instance.post(`/admin/meilisearch/auto-complete/setup`);
    return response.data;
}

/* getRecentSearchLogs */

export const getRecentSearchLogs = async () => {
    const response = await instance.get(`/medicine/search-logs`);
    return response.data;
}

/* deleteSearchLog */

interface IDeleteSearchLogProps {
    searchLogId: string;
}
export const deleteSearchLog = async ({ searchLogId }:IDeleteSearchLogProps) => {
    const response = await instance.delete(`/medicine/search-logs/${searchLogId}`);
    return response.data;
}

/* delete all search logs */

export const deleteAllSearchLogs = async () => {
    const response = await instance.delete(`/medicine/search-logs`);
    return response.data;
}

/* search medicines */

interface ISearchMedicinesProps {
    page: number;
    size: number;
    searchKeyword: string | null;
    packageType: string | null;
    packageUnit: string | null;
    isSameIngredientCodeGrouping: boolean;
}
export const searchMedicines = async ({ page, size, searchKeyword, packageType, packageUnit, isSameIngredientCodeGrouping }:ISearchMedicinesProps) => {
    const response = await instance.get(`/medicine/search`, {
        params: { 
            page, 
            size, 
            searchKeyword,
            packageType,
            packageUnit,
            isSameIngredientCodeGrouping
        }
    });
    return response.data;
}

/* create search filter by ai */

interface ICreateSearchFilterByAiProps {
    sentence: string | null;
}
export const createSearchFilterByAi = async ({ sentence }:ICreateSearchFilterByAiProps) => {
    const response = await instance.get(`/medicine/search-ai/filter`, {
        params: { sentence }
    });
    return response.data;
}

/* search medicines by ai */

interface ISearchMedicinesByAiProps {
    page: number;
    size: number;
    filter: IAiSearchFilter;
    packageType: string | null;
    packageUnit: string | null;
    isSameIngredientCodeGrouping: boolean;
}
export const searchMedicinesByAi = async ({ page, size, filter, packageType, packageUnit, isSameIngredientCodeGrouping }: ISearchMedicinesByAiProps) => {
    // GET params에는 중첩 객체가 쿼리스트링으로 잘 직렬화되지 않으므로, JSON 문자열로 전달합니다. (백엔드에서 파싱 필요)
    const response = await instance.get(`/medicine/search-ai`, {
        params: { 
            page, 
            size, 
            filter: JSON.stringify(filter),
            packageType,
            packageUnit,
            isSameIngredientCodeGrouping
        },
    });
    return response.data;
};

/* get meilisearch auto-complete */

interface IGetMeilisearchAutoCompleteProps {
    searchKeyword: string;
}
export const getMeilisearchAutoComplete = async ({ searchKeyword }:IGetMeilisearchAutoCompleteProps) => {
    const response = await instance.get(`/medicine/search/auto-complete`, {
        params: { searchKeyword }
    });
    return response.data;
}

/* get search hot keywords */

export const getSearchHotKeywords = async () => {
    const response = await instance.get(`/medicine/search/hot-keywords`);
    return response.data;
}

/* admin create connections by ai */

interface IAdminCreateConnectionsByAiProps {
    medicineId: string;
}
export const adminCreateConnectionsByAi = async ({ medicineId }:IAdminCreateConnectionsByAiProps) => {
    const response = await instance.post(`/admin/connections/${medicineId}/generate-by-ai`);
    return response.data;
}

/* admin get ingredients */

interface IAdminGetIngredientsProps {
    page: number;
    size: number;
    keyword: string | null;
}
export const adminGetIngredients = async ({ page, size, keyword }:IAdminGetIngredientsProps) => {
    const response = await instance.get(`/admin/ingredients`, {
        params: { page, size, keyword }
    });
    return response.data;
}












/* get classification distinct */

export const getClassificationDistinct = async () => {
    const response = await instance.get(`/admin/classification-distinct`);
    return response.data;
}

/* admin generate fields */

export const adminGenerateFields = async () => {
    const response = await instance.post(`/admin/generate-fields`);
    return response.data;
}

/* admin create Companies */

export const adminCreateCompanies = async () => {
    const response = await instance.post(`/admin/create-companies`);
    return response.data;
}










/* admin get Profiles */

interface IAdminGetProfilesProps {
    page: number;
    size: number;
    searchKeyword: string | null;
    isSuspendedOnly?: boolean;
}
export const adminGetProfiles = async ({ page, size, searchKeyword, isSuspendedOnly }:IAdminGetProfilesProps) => {
    const response = await instance.get(`/admin/profiles`, {
        params: { page, size, searchKeyword, isSuspendedOnly }
    });
    return response.data;
}

/* admin get chats */

interface IAdminGetChatsProps {
    page: number;
    size: number;
    isReportedOnly?: boolean;
    isHiddenOnly?: boolean;
}
export const adminGetChats = async ({ page, size, isReportedOnly, isHiddenOnly }:IAdminGetChatsProps) => {
    const response = await instance.get(`/admin/chats`, {
        params: { page, size, isReportedOnly, isHiddenOnly }
    });
    return response.data;
}

/* admin get comments */

interface IAdminGetCommentsProps {
    page: number;
    size: number;
    isReportedOnly?: boolean;
    isHiddenOnly?: boolean;
}
export const adminGetComments = async ({ page, size, isReportedOnly, isHiddenOnly }:IAdminGetCommentsProps) => {
    const response = await instance.get(`/admin/comments`, {
        params: { page, size, isReportedOnly, isHiddenOnly }
    });
    return response.data;
}

/* admin get replies */

interface IAdminGetRepliesProps {
    page: number;
    size: number;
    isReportedOnly?: boolean;
    isHiddenOnly?: boolean;
}
export const adminGetReplies = async ({ page, size, isReportedOnly, isHiddenOnly }:IAdminGetRepliesProps) => {
    const response = await instance.get(`/admin/replies`, {
        params: { page, size, isReportedOnly, isHiddenOnly }
    });
    return response.data;
}

/* admin delete profiles */

interface IAdminDeleteProfilesProps {
    profileIds: string[];
}
export const adminDeleteProfiles = async ({ profileIds }:IAdminDeleteProfilesProps) => {
    const response = await instance.delete(`/admin/profiles`, {
        data: { profileIds }
    });
    return response.data;
}

/* admin suspend profiles */

interface IAdminSuspendProfilesProps {
    profileId: string;
    reason: string;
}
export const adminSuspendProfiles = async ({ profileId, reason }:IAdminSuspendProfilesProps) => {
    const response = await instance.post(`/admin/profiles/${profileId}/suspend`, { reason });
    return response.data;
}

/* admin delete suspend */

interface IAdminDeleteSuspendProps {
    profileId: string;
    suspendId: string;
}
export const adminDeleteSuspend = async ({ profileId, suspendId }:IAdminDeleteSuspendProps) => {
    const response = await instance.delete(`/admin/profiles/${profileId}/suspend/${suspendId}`);
    return response.data;
}

/* admin delete chats */

interface IAdminDeleteChatsProps {
    chatIds: string[];
}
export const adminDeleteChats = async ({ chatIds }:IAdminDeleteChatsProps) => {
    const response = await instance.delete(`/admin/chats`, {
        data: { chatIds }
    });
    return response.data;
}

/* admin delete comments */

interface IAdminDeleteCommentsProps {
    commentIds: string[];
}
export const adminDeleteComments = async ({ commentIds }:IAdminDeleteCommentsProps) => {
    const response = await instance.delete(`/admin/comments`, {
        data: { commentIds }
    });
    return response.data;
}

/* admin delete replies */

interface IAdminDeleteRepliesProps {
    replyIds: string[];
}
export const adminDeleteReplies = async ({ replyIds }:IAdminDeleteRepliesProps) => {
    const response = await instance.delete(`/admin/replies`, {
        data: { replyIds }
    });
    return response.data;
}

/* admin update chat status */

interface IAdminUpdateChatStatusProps {
    chatId:string;
    status:"NORMAL" | "HOLD" | "HIDDEN";
}

export const adminUpdateChatStatus = async ({ chatId, status }:IAdminUpdateChatStatusProps) => {
    const response = await instance.put(`/admin/chats/${chatId}/status`, { status });
    return response.data;
}

/* admin update chat hidden reason */

interface IAdminUpdateChatHiddenReasonProps {
    chatId: string;
    hiddenReason: string;
}
export const adminUpdateChatHiddenReason = async ({ chatId, hiddenReason }: IAdminUpdateChatHiddenReasonProps) => {
    const response = await instance.put(`/admin/chats/${chatId}/hidden-reason`, { hiddenReason });
    return response.data;
}


/* admin update comment status */

interface IAdminUpdateCommentStatusProps {
    commentId:string;
    status:"NORMAL" | "HOLD" | "HIDDEN";
}

export const adminUpdateCommentStatus = async ({ commentId, status }:IAdminUpdateCommentStatusProps) => {
    const response = await instance.put(`/admin/comments/${commentId}/status`, { status });
    return response.data;
}

/* admin update comment hidden reason */

interface IAdminUpdateCommentHiddenReasonProps {
    commentId: string;
    hiddenReason: string;
}
export const adminUpdateCommentHiddenReason = async ({ commentId, hiddenReason }: IAdminUpdateCommentHiddenReasonProps) => {
    const response = await instance.put(`/admin/comments/${commentId}/hidden-reason`, { hiddenReason });
    return response.data;
}

/* admin update reply status */

interface IAdminUpdateReplyStatusProps {
    replyId:string;
    status:"NORMAL" | "HOLD" | "HIDDEN";
}

export const adminUpdateReplyStatus = async ({ replyId, status }:IAdminUpdateReplyStatusProps) => {
    const response = await instance.put(`/admin/replies/${replyId}/status`, { status });
    return response.data;
}

/* admin update reply hidden reason */

interface IAdminUpdateReplyHiddenReasonProps {
    replyId: string;
    hiddenReason: string;
}
export const adminUpdateReplyHiddenReason = async ({ replyId, hiddenReason }: IAdminUpdateReplyHiddenReasonProps) => {
    const response = await instance.put(`/admin/replies/${replyId}/hidden-reason`, { hiddenReason });
    return response.data;
}


















/* ------------------------------------------------------------------- */

/* admin temp action */

const readAttachmentFileName = (disposition: unknown) => {
    if (typeof disposition !== "string") return null;

    const matched = /filename="([^"]+)"/.exec(disposition);
    return matched?.[1] ?? null;
};

export const adminTempAction = async () => {
    const response = await instance.post(`/admin/temp-action`);
    const csv = response.data;

    if (typeof csv !== "string") {
        throw new Error("INVALID_CSV_RESPONSE");
    }

    return {
        csv,
        fileName: readAttachmentFileName(response.headers["content-disposition"]) ?? "medicines-display.csv",
    };
};

/* admin get analysis searching */

export const adminGetAnalysisSearching = async () => {
    const response = await instance.get(`/admin/analysis/search`);
    return response.data;
}

/* admin get logs */

interface IAdminGetSearchLogsProps {
    page:number;
    size:number;
    keyword:string | null;

    isNoResult: boolean | null;
    isChecked: boolean | null;
}
export const adminGetSearchLogs = async ({ page, size, keyword, isNoResult, isChecked }:IAdminGetSearchLogsProps) => {
    const response = await instance.get("/admin/analysis/search-logs", {
        params: {
            page,
            size,
            keyword,
            isNoResult,
            isChecked
        }
    });
    return response.data;
}

/* admin update search log checked */

interface IAdminUpdateSearchLogCheckedProps {
    searchLogId: string;
}
export const adminUpdateSearchLogChecked = async ({ searchLogId }:IAdminUpdateSearchLogCheckedProps) => {
    const response = await instance.put(`/admin/analysis/search-logs/${searchLogId}/checked`);
    return response.data;
}

/* admin get analysis entry */

export const adminGetAnalysisEntry = async () => {
    const response = await instance.get(`/admin/analysis/entry`);
    return response.data;
}

/* admin get medicines by entry logs */

interface IAdminGetMedicinesByEntryLogsProps {
    page: number;
    size: number;
    searchKeyword: string | null;
}
export const adminGetMedicinesByEntryLogs = async ({ page, size, searchKeyword }:IAdminGetMedicinesByEntryLogsProps) => {
    const response = await instance.get(`/admin/analysis/entry-logs/medicines`, {
        params: { page, size, searchKeyword }
    });
    return response.data;
}

/* admin get medicine by entry log */

interface IAdminGetMedicineByEntryLogProps {
    medicineId: string;
}
export const adminGetMedicineByEntryLog = async ({ medicineId }:IAdminGetMedicineByEntryLogProps) => {
    const response = await instance.get(`/admin/analysis/entry-logs/medicines/${medicineId}`);
    return response.data;
}

/* admin get analysis chat */

export const adminGetAnalysisChat = async () => {
    const response = await instance.get(`/admin/analysis/chat`);
    return response.data;
}


/* admin get analysis trend */

interface IAdminGetAnalysisTrendProps {
    unit: "day" | "month";
}

export const adminGetAnalysisTrend = async ({ unit }:IAdminGetAnalysisTrendProps) => {
    const response = await instance.get(`/admin/analysis/trend`, {
        params: {
            unit
        }
    });
    return response.data;
}